import { useEffect, useState, useCallback } from "react";
import { SalvoContext } from "./SalvoContext";
import { useAuth0 } from "@auth0/auth0-react";
import { jwtDecode } from "jwt-decode";
import usePersistentState from "../Hooks/usePeristentState";
import { useJsApiLoader } from "@react-google-maps/api";
import { getBillingInfo, getCompany, getGeneralSettingsForCompany } from "../API/companyApi";
import { getClaimsForUser } from "../API/usersApi";

const libraries = ["places"];

const SalvoContextProvider = ({ children }) => {
    const [copiedGeofence, setCopiedGeofence] = useState()
    // The company ID that the user is currently viewing.
    // Usually this is the same as 'company', but if the user is admin and is impersonating another company, this will be different.
    // Note: This holds the whole company object, not only the id.
    const [selectedCompany, setSelectedCompany] = usePersistentState('IMPERSONATED_COMPANY', null)
    const [impersonatedUser, setImpersonatedUser] = usePersistentState('IMPERSONATED_USER', null);

    const [impersonatedClaims, setImpersonatedClaims] = useState(null);

    // The company ID of the authenticated user.
    const [companyId, setCompanyId] = useState(null);
    const [isImpersonationValidated, setIsImpersonationValidated] = useState(false);

    // The company fetched from the backend with more details such as name.
    const [company, setCompany] = useState(null);
    const [companyError, setCompanyError] = useState(null);
    const [companyRetryToken, setCompanyRetryToken] = useState(0);
    const [billingInfo, setBillingInfo] = useState(null);
    const [trialInfo, setTrialInfo] = useState(null);

    const [geofenceProjectReference, setGeofenceProjectReference] = useState(null);

    const activeCompanyId = selectedCompany ? selectedCompany.id : companyId;

    const { getAccessTokenSilently, isAuthenticated } = useAuth0();

    useEffect(() => {
        const fetchCompanyId = async () => {
            if (!isAuthenticated) {
                setIsImpersonationValidated(false);
                return;
            }

            setIsImpersonationValidated(false);

            try {
                const token = await getAccessTokenSilently();
                const decodedToken = jwtDecode(token);

                if (
                    (selectedCompany || impersonatedUser)
                ) {
                    setSelectedCompany(null);
                    setImpersonatedUser(null);
                    setImpersonatedClaims(null);
                }

                // setImpersonationOwner(user.sub);
                setCompanyId(decodedToken.company_id);
                setIsImpersonationValidated(true);
            } catch (error) {
                console.error('Error fetching company ID:', error);
                setIsImpersonationValidated(false);
            }
        };

        fetchCompanyId();

        // intentionally validate persisted impersonation only when the authenticated Auth0 identity changes.
    }, [
        getAccessTokenSilently,
        isAuthenticated
    ]);

    useEffect(() => {
        const fetchCompany = async () => {
            if (!isImpersonationValidated) {
                return;
            }

            if (selectedCompany || companyId) {
                setCompanyError(null);

                try {
                    const response = await getCompany(
                        selectedCompany ? selectedCompany.id : companyId
                    );

                    setCompany(response.data);
                } catch (error) {
                    console.error('Error fetching company:', error);
                    setCompany(null);
                    setCompanyError(error);
                }
            }
        };

        fetchCompany();
    }, [
        selectedCompany,
        companyId,
        companyRetryToken,
        isImpersonationValidated
    ]);

    useEffect(() => {
        setTrialInfo(selectedCompany?.trialPeriod);
    }, [selectedCompany]);

    useEffect(() => {
        const fetchBillingInfo = async () => {
            if (!isImpersonationValidated) {
                return;
            }

            if (companyId || selectedCompany) {
                try {
                    const response = await getBillingInfo(
                        selectedCompany ? selectedCompany.id : companyId
                    );

                    setBillingInfo(response.data);
                } catch (error) {
                    console.error('Error fetching billing info:', error);
                }
            }
        };

        fetchBillingInfo();
    }, [
        companyId,
        selectedCompany,
        isImpersonationValidated
    ]);

    useEffect(() => {
        const fetchImpersonatedClaims = async (userId) => {
            try {
                const response = await getClaimsForUser(userId);
                setImpersonatedClaims(response.data);
            } catch (error) {
                console.error('Error fetching impersonated user claims:', error);
            }
        }

        if (impersonatedUser?.id) {
            fetchImpersonatedClaims(impersonatedUser.id);
        }
        else {
            setImpersonatedClaims(null);
        }
    }, [impersonatedUser]);


    const refreshGeofenceProjectReference = useCallback(async () => {
        if (!activeCompanyId) return;

        try {
            const response = await getGeneralSettingsForCompany(activeCompanyId);

            const setting = response.data?.find(
                s => s.key === "GEOFENCE_PROJECT_REFERENCE"
            );

            setGeofenceProjectReference(setting?.value ?? null);
        } catch (error) {
            console.error("Error fetching GEOFENCE_PROJECT_REFERENCE:", error);
            setGeofenceProjectReference(null);
        }
    }, [activeCompanyId]);

    useEffect(() => {
        refreshGeofenceProjectReference();
    }, [refreshGeofenceProjectReference]);


    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: "AIzaSyCZult5Wi6jfRx2pMQjMXmCHewmv2O8fD4",
        libraries
    });

    return (
        <SalvoContext.Provider value={{
            copiedGeofence, setCopiedGeofence,
            selectedCompany, setSelectedCompany,
            companyId,
            company,
            companyError,
            retryFetchCompany: () => setCompanyRetryToken((t) => t + 1),
            isLoaded,
            trialInfo,
            setTrialInfo,
            billingInfo, setBillingInfo,
            impersonatedUser, setImpersonatedUser,
            impersonatedClaims,
            geofenceProjectReference,
            refreshGeofenceProjectReference
        }}>
            {children}
        </SalvoContext.Provider>
    );
};

export default SalvoContextProvider